import Table, { RowProps } from "@/shared/data-display/table";
import { DeleteButton, DragButton } from "@/shared/form/button";
import { AddStepButton } from "./add-step-button";
import { v4 as uuidv4 } from "uuid";
import Form from "@/shared/form/form";
import { createContext, useContext, useMemo } from "react";
import { useTranslations } from "use-intl";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { FormListProps } from "antd/es/form";
import { ProcessRouteStepInput } from "@/shared/form/process-route-step-input";
import { RouteRoleOptionFragment } from "gql/graphql";

type PathInputProps = Omit<FormListProps, "children" | "name"> & {
  name: string;
  roles?: RouteRoleOptionFragment[];
};

export function PathInput({ roles, ...props }: PathInputProps) {
  const form = Form.useFormInstance();
  const t = useTranslations("PathInput");

  return (
    <Form.List {...props}>
      {(fields, { add, remove, move }, { errors }) => {
        const source = fields.map((field) => {
          return {
            id: form.getFieldValue([props.name, field.name]).id,
            ...field,
          };
        });

        return (
          <div className="mb-[--ant-form-item-margin-bottom]">
            <div className="flex justify-end">
              <AddStepButton
                className="!justify-end"
                onClick={() => add({ id: uuidv4() })}
              />
            </div>
            <div className="mt-4">
              <DndContext
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={({ active, over }) => {
                  const activeIndex = source.findIndex(
                    (field) => field.id === active.id,
                  );
                  const overIndex = source.findIndex(
                    (field) => field.id === over?.id,
                  );

                  move(activeIndex, overIndex);
                }}
              >
                <SortableContext
                  strategy={verticalListSortingStrategy}
                  items={source}
                >
                  <Table
                    pagination={false}
                    dataSource={source}
                    components={{ body: { row: Row } }}
                    rowKey={(record) => record.id}
                    columns={[
                      {
                        key: "sort",
                        align: "center",
                        title: t("Sort"),
                        width: 30,
                        render: () => <OrderButton />,
                      },
                      {
                        key: "input",
                        title: t("Role") + " / " + t("Person"),
                        render: (_, field) => {
                          return (
                            <ProcessRouteStepInput
                              itemPath={[field.name]}
                              listPath={[props.name]}
                              roles={roles}
                            />
                          );
                        },
                      },
                      {
                        key: "delete",
                        align: "center",
                        width: 30,
                        title: t("Delete"),
                        render: (_, field) => (
                          <DeleteButton onClick={() => remove(field.name)} />
                        ),
                      },
                    ]}
                  />
                </SortableContext>
              </DndContext>

              <Form.ErrorList
                errors={errors}
                className="text-[--ant-color-error]"
              />
            </div>
          </div>
        );
      }}
    </Form.List>
  );
}

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}
const RowContext = createContext<RowContextProps>({});

function Row(props: RowProps & { "data-row-key": string }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    setActivatorNodeRef,
  } = useSortable({
    id: props["data-row-key"],
  });

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr
        {...props}
        ref={setNodeRef}
        {...attributes}
        style={{
          ...props.style,
          transform: CSS.Translate.toString(transform),
          transition,
        }}
      />
    </RowContext.Provider>
  );
}

function OrderButton() {
  const { listeners } = useContext(RowContext);

  return <DragButton {...listeners} />;
}
